// Per-route SEO meta (PERF-01 item D). Hand-rolled rather than pulling in
// @vueuse/head or vue-meta — neither is a dependency yet, and all we need is
// `document.title` + one <meta name="description"> tag kept in sync with the
// active route's `meta.title` / `meta.description` (see router/index.ts).
const DEFAULT_TITLE = 'Eskay Science';
const DEFAULT_DESCRIPTION =
  'Eskay Science is a subscription classroom platform with video lessons, interactive simulations and printable resources for students, teachers and admins.';

function upsertMeta(name: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertOgMeta(property: string, content: string): void {
  let tag = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonicalLink(href: string): void {
  let tag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/** Applies a route's title/description/canonical URL to the document,
 * falling back to the site-wide defaults declared in index.html for routes
 * that don't set them. `path` is the current route's path (no query string)
 * so every route — not just `/` — gets its own canonical <link> and og:url,
 * per SEO audit findings on duplicate canonicals across routes. */
export function applyPageMeta(meta: { title?: string; description?: string; path?: string }): void {
  const title = meta.title ? `${meta.title} · Eskay Science` : DEFAULT_TITLE;
  const description = meta.description ?? DEFAULT_DESCRIPTION;
  const path = meta.path ?? window.location.pathname;
  const canonicalUrl = new URL(path, window.location.origin).toString();

  document.title = title;
  upsertMeta('description', description);
  upsertOgMeta('og:title', title);
  upsertOgMeta('og:description', description);
  upsertOgMeta('og:url', canonicalUrl);
  upsertCanonicalLink(canonicalUrl);
}
